/** @jsxImportSource @emotion/react */
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { useDesignTokens } from '@hooks/useDesignTokens'
import { TOKENS } from '@gen/tokenConstants'
import { CalendarData, CalendarView, TimeSlot } from '@/types/calendar'
import { useState } from 'react'
import BookingModal from './BookingModal'

interface CalendarDisplayProps {
  calendarData: CalendarData
  currentDate: Date
  view: CalendarView
}

function getCalendarView(view: CalendarView): string {
  if (view === 'day') return 'timeGridDay'
  if (view === 'week') return 'timeGridWeek'
  return 'dayGridMonth'
}

export default function CalendarDisplay({
  calendarData,
  currentDate,
  view,
}: CalendarDisplayProps) {
  const { getValue } = useDesignTokens()
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
    null,
  )

  const events = calendarData.events.map((event) => {
    const isBooking = event.type === 'booking'
    return {
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      backgroundColor: isBooking
        ? getValue(TOKENS.color.primary[500])
        : getValue(TOKENS.color.neutral[400]),
      borderColor: isBooking
        ? getValue(TOKENS.color.primary[600])
        : getValue(TOKENS.color.neutral[500]),
    }
  })

  function handleDateClick(info: any) {
    const clickedDate = info.date
    const timeSlot = calendarData.timeSlots.find((slot) => {
      const slotStart = new Date(slot.start)
      return (
        slotStart.getTime() >= clickedDate.getTime() &&
        slotStart.getTime() < clickedDate.getTime() + 30 * 60 * 1000
      )
    })

    if (timeSlot && timeSlot.available) {
      setSelectedTimeSlot(timeSlot)
    }
  }

  function handleSelect(info: any) {
    const startDate = info.start
    const endDate = info.end

    const newTimeSlot: TimeSlot = {
      id: `temp-${Date.now()}`,
      start: startDate,
      end: endDate,
      available: true,
    }

    setSelectedTimeSlot(newTimeSlot)
  }

  return (
    <div
      css={{
        '& .fc': {
          fontFamily: getValue(TOKENS.typography.fontFamily.primary),
          fontSize: getValue(TOKENS.typography.fontSize.sm),
        },
        '& .fc-theme-standard td': {
          borderColor: getValue(TOKENS.color.primary[200]),
        },
        '& .fc-theme-standard th': {
          borderColor: getValue(TOKENS.color.primary[200]),
          backgroundColor: getValue(TOKENS.color.primary[50]),
          color: getValue(TOKENS.color.primary[800]),
          fontWeight: getValue(TOKENS.typography.fontWeight.semibold),
        },
        '& .fc-daygrid-day': {
          '&:hover': {
            backgroundColor: getValue(TOKENS.color.primary[50]),
            cursor: 'pointer',
          },
        },
        '& .fc-today': {
          backgroundColor: `${getValue(TOKENS.color.primary[100])} !important`,
        },
        '& .fc-button-primary': {
          backgroundColor: getValue(TOKENS.color.primary[600]),
          borderColor: getValue(TOKENS.color.primary[600]),
          '&:hover': {
            backgroundColor: getValue(TOKENS.color.primary[700]),
            borderColor: getValue(TOKENS.color.primary[700]),
          },
        },
      }}
    >
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={getCalendarView(view)}
        initialDate={currentDate}
        events={events}
        dateClick={handleDateClick}
        select={handleSelect}
        headerToolbar={{
          left: '',
          center: '',
          right: '',
        }}
        height='auto'
        slotMinTime='09:00:00'
        slotMaxTime='18:00:00'
        slotDuration='00:30:00'
        snapDuration='00:15:00'
        allDaySlot={false}
        nowIndicator={true}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={true}
        weekends={true}
      />

      {selectedTimeSlot && (
        <BookingModal
          timeSlot={selectedTimeSlot}
          onClose={() => setSelectedTimeSlot(null)}
          onConfirm={(userDetails) => {
            console.log('Booking confirmed:', {
              timeSlot: selectedTimeSlot,
              userDetails,
            })
            setSelectedTimeSlot(null)
          }}
        />
      )}
    </div>
  )
}
